import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Transition, TransitionGroup } from "react-transition-group";
import { useCombineRefs } from "../hooks/useCombineRefs";
import { usePortalRoot } from "../portal-root/PortalRootContext";
import { findTopMostDialog } from "../dialog/findTopMostDialog";
import { ToastMessage } from "./ToastMessage";
import * as Styled from "./Toast.styled";
import { TRANSITION_DURATION } from "./ToastMessage.styled";
const ToastMessageContainer = forwardRef(({ message, shouldAutoFocus, isDismissible, advance, onDismiss, autoFocusMessage, previousFocus, placement, portalTarget, transitionState, ...otherProps }, ref) => {
    const toastType = portalTarget ? "portal" : "default";
    const toastMessageRef = useRef(null);
    const { onDismiss: messageOnDismiss, ...messageProps } = message;
    useEffect(() => {
        autoFocusMessage?.();
        if (toastMessageRef.current) {
            // TODO: Might not be needed if we implement a global Escape handler since message is already announced via role="alert"
            // TODO: DES-3469 - Should only focus if manual dismissal / shouldAutoFocus is enabled
            toastMessageRef.current.focus();
        }
    }, [autoFocusMessage]);
    return (_jsx(Styled.Toast, { ref: ref, "data-testid": "Toast", ...otherProps, toastType: toastType, placement: placement, children: _jsx(ToastMessage, { toastMessageRef: toastMessageRef, isDismissible: isDismissible, onDismiss: () => {
                advance();
                onDismiss(message.key);
                messageOnDismiss && messageOnDismiss(message.key);
            }, placement: placement, toastType: toastType, transitionState: transitionState, ...messageProps }) }));
});
export const Toast = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { isDismissible = true, messages = [], onDismiss = () => { }, onEmpty = () => { }, placement = "top", portalTarget, shouldAutoFocus: shouldAutoFocusProp, ...otherProps } = props;
    // see DES-3469
    const shouldAutoFocus = true;
    const portalRoot = usePortalRoot();
    const [queue, setQueue] = useState([]);
    const [dismissed, setDismissed] = useState([]);
    const initialFocusRef = useRef(null);
    useEffect(() => {
        let newMessages = [];
        if (messages.length > 0) {
            // find messages that are new - not already in queue or dismissed list based on key
            newMessages = messages
                .filter(({ key }) => !queue.some((n) => key === n.key))
                .filter(({ key }) => !dismissed.some((n) => key === n.key));
        }
        // remove items from queue that have shouldAutoDismiss="when-removed-from-props", a key, and no longer exist in messages prop
        const nextQueue = queue.filter(({ key, shouldAutoDismiss }) => key === undefined || shouldAutoDismiss !== "when-removed-from-props" || messages.some((n) => key === n.key));
        setQueue(nextQueue.concat(newMessages));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, dismissed]);
    const autoFocusMessage = useCallback(() => {
        if (document.body !== document.activeElement &&
            (!initialFocusRef.current || !document.body.contains(initialFocusRef.current))) {
            initialFocusRef.current = document.activeElement;
        }
    }, []);
    const restoreInitialFocus = () => {
        const restoreNode = initialFocusRef.current;
        initialFocusRef.current = null;
        requestAnimationFrame(() => {
            if (
            // Only restore focus if nothing else is focused (e.g. in the onDismiss handler)
            document.activeElement !== document.body &&
                // or focus is still in the unmounting toast
                (!nodeRef.current || !nodeRef.current.contains(document.activeElement))) {
                return;
            }
            // If restoreNode isn't attached to DOM, try to focus the currently opened dialog
            if (!restoreNode || !document.body.contains(restoreNode)) {
                // We're most probably in a dialog, so find the topmost one which isn't hidden itself
                const dialog = findTopMostDialog(portalRoot ?? document.body);
                if (dialog) {
                    dialog.focus();
                    return;
                }
            }
            // DES-3517 - restore focus to the original node, but only if it's not obscured by aria-hidden (used in Dialog)
            // otherwise try to find a different suitable focus target
            if (restoreNode?.closest(`[aria-hidden="true"]`)) {
                // We're most probably in a dialog, so find the topmost one which isn't hidden itself
                const dialog = findTopMostDialog(portalRoot ?? document.body);
                if (dialog) {
                    dialog.focus();
                    return;
                }
            }
            // We are probably better off restoring focus SOMEWHERE, so reuse the initialFocusRef
            // in case there are no dialogs and we're somewhere else, and the app can run any fallback logic in `onDismiss`.
            restoreNode?.focus();
        });
    };
    const getNextState = () => {
        const newDismissed = queue.pop();
        let sanitizedDismissed = dismissed;
        let shouldRestoreFocus = false;
        if (newDismissed) {
            sanitizedDismissed = [newDismissed, ...dismissed];
            if (queue.length === 0) {
                onEmpty();
                shouldRestoreFocus = true;
            }
        }
        return {
            queue,
            sanitizedDismissed,
            shouldRestoreFocus
        };
    };
    const advance = () => {
        const { queue, sanitizedDismissed, shouldRestoreFocus } = getNextState();
        setQueue(queue);
        setDismissed(sanitizedDismissed);
        if (shouldRestoreFocus) {
            restoreInitialFocus();
        }
    };
    // react-transition-group transition ref object that should point to the transitioning child
    const nodeRef = React.useRef(null);
    // combining ref and nodeRef
    const combinedRefs = useCombineRefs(ref, nodeRef);
    const createToastMessage = (message) => (_jsx(Transition, { timeout: TRANSITION_DURATION, nodeRef: nodeRef, children: (transitionState) => (_jsx(ToastMessageContainer, { message: message, shouldAutoFocus: shouldAutoFocus, isDismissible: isDismissible, advance: advance, onDismiss: onDismiss, placement: placement, portalTarget: portalTarget, autoFocusMessage: shouldAutoFocus ? autoFocusMessage : undefined, transitionState: transitionState, ref: combinedRefs, ...otherProps })) }, message.key ?? message.id));
    const next = queue[queue.length - 1];
    const toast = (_jsx(TransitionGroup, { "data-testid": "ToastContainer", "data-always-allow-focus": "true", "aria-live": "off", appear: true, ...otherProps, children: next && createToastMessage(next) }));
    return portalTarget ? ReactDOM.createPortal(toast, portalTarget) : toast;
});
Toast.displayName = "Toast";
Toast.defaultProps = {
    isDismissible: true,
    messages: [],
    onDismiss: () => { },
    onEmpty: () => { },
    placement: "top",
    shouldAutoFocus: true
};
